import { useRouteError } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { isChunkLoadError, reloadForNewVersion, reloadedRecently } from '@/lib/app-reload'

// Boundary gốc của router. Hai loại lỗi:
//   1. Lỗi tải chunk sau DEPLOY (hash file đổi) — KHÔNG phải lỗi thật: tự tải lại
//      trang 1 lần (guard chống lặp) để lấy bản mới → user không thấy màn hình lỗi.
//   2. Lỗi ứng dụng thật — hiện thẻ lỗi gọn + nút "Tải lại trang".
export function RootErrorBoundary() {
  const error = useRouteError()

  if (isChunkLoadError(error)) {
    // Chưa tải lại gần đây → kích hoạt reload, hiện spinner trong lúc chờ.
    if (!reloadedRecently()) {
      reloadForNewVersion()
      return <FullScreenMessage spinner title="Đang cập nhật phiên bản mới…" />
    }
    // Vừa tải lại mà vẫn lỗi → có thể mạng/hệ thống trục trặc: hiện nút thủ công.
    return (
      <ErrorCard
        title="Chưa tải được phiên bản mới"
        description="Có bản cập nhật nhưng trình duyệt chưa tải được. Kiểm tra mạng rồi thử tải lại."
      />
    )
  }

  return (
    <ErrorCard
      title="Đã xảy ra lỗi"
      description="Trang gặp sự cố ngoài dự kiến. Bạn hãy tải lại; nếu vẫn lỗi, báo kỹ thuật."
      detail={error instanceof Error ? error.message : undefined}
    />
  )
}

function FullScreenMessage({
  title,
  spinner = false,
}: {
  title: string
  spinner?: boolean
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-6">
      <div className="flex flex-col items-center gap-3 text-center">
        {spinner && <Spinner />}
        <p className="text-sm text-fg-muted">{title}</p>
      </div>
    </div>
  )
}

function ErrorCard({
  title,
  description,
  detail,
}: {
  title: string
  description: string
  detail?: string
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface-elevated p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-fg">{title}</h1>
        <p className="mt-2 text-sm text-fg-muted">{description}</p>
        {detail && (
          <p className="mt-3 break-words rounded-md bg-surface-muted px-3 py-2 text-left font-mono text-[11px] text-fg-subtle">
            {detail}
          </p>
        )}
        <div className="mt-5">
          <Button onClick={() => window.location.reload()}>Tải lại trang</Button>
        </div>
      </div>
    </div>
  )
}
